import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Document as ReactPdfDocument, Page } from 'react-pdf'
import styled, { css } from 'styled-components'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import CloseIcon from '@mui/icons-material/Close'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import ViewListIcon from '@mui/icons-material/ViewList'
import ViewStreamIcon from '@mui/icons-material/ViewStream'
import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CircularProgress from '@mui/material/CircularProgress'
import { pathOr } from 'ramda'
import { markFileAsViewed } from 'services/fileService'
import ReactDOM from 'react-dom'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'

const PdfFullScreenViewer = ({ open, onClose, file, version, initialSearchQuery = '', skipAccessLog = false }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [isOverflowingHorizontallyInSingleView, setIsOverflowingHorizontallyInSingleView] = useState(false)
  const [isOverflowingVerticallyInSingleView, setIsOverflowingVerticallyInSingleView] = useState(false)

  const [rotation, setRotation] = useState(0)
  const [scale, setScale] = useState(1)
  const [initialScaleSet, setInitialScaleSet] = useState(false)
  const [hasManualZoom, setHasManualZoom] = useState(false)
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const timeoutRef = useRef(null)
  const [isDocumentReady, setIsDocumentReady] = useState(false)
  const lastPageChangeRef = useRef(0)
  const zoomTimeout = useRef(null)
  const isPageLoadingRef = useRef(false)
  const lastAutoFitKeyRef = useRef(null)

  const [showThumbnails, setShowThumbnails] = useState(false)
  const [viewMode, setViewMode] = useState('single')
  const [searchText, setSearchText] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [currentMatch, setCurrentMatch] = useState(0)
  const [highlightCount, setHighlightCount] = useState(0)
  const [showSearch, setShowSearch] = useState(false)
  const textItemsRef = useRef({})
  const pdfDocumentRef = useRef(null)
  const searchTextRef = useRef('')

  const scrollContainerRef = useRef(null)
  const shouldCenterAfterZoomRef = useRef(false)
  const dragStateRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  })
  const [isDragging, setIsDragging] = useState(false)
  const searchInputRef = useRef(null)
  const searchBarRef = useRef(null)
  const searchTimeout = useRef(null)
  const hasNavigatedRef = useRef(false)
  const intersectionDebounceRef = useRef(null)

  const [hasExtractedText, setHasExtractedText] = useState(false)

  const normalizeForSearch = useCallback((value) => {
    return (value || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]/gi, '')
  }, [])

  const clearDomHighlights = useCallback(() => {
    document.querySelectorAll('.pdf-search-highlight-overlay').forEach((el) => el.remove())
  }, [])

  const applyDomHighlights = useCallback((query) => {
    clearDomHighlights()

    if (!query || query.trim().length < 2) {
      setHighlightCount(0)
      return
    }

    const trimmedQuery = query.trim()
    const escaped = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    // Word boundary at START only — prevents "50" matching inside "1500"
    // but still allows prefix matching ("pow" finds "Power")
    const isAlphanumeric = /^[a-z0-9]+$/i.test(trimmedQuery)
    const regex = isAlphanumeric
      ? new RegExp(`\\b${escaped}`, 'i')
      : new RegExp(escaped, 'i')
    const globalRegex = new RegExp(regex.source, 'gi')

    let totalMatches = 0
    const textLayers = document.querySelectorAll('.react-pdf__Page__textContent')

    textLayers.forEach((layer) => {
      // Append highlights to the page container (no transforms) instead of text layer
      const page = layer.closest('.react-pdf__Page')
      if (!page) return

      const pageRect = page.getBoundingClientRect()
      // Safety: account for any transforms on the page element
      const scaleX = page.offsetWidth > 0 ? page.offsetWidth / pageRect.width : 1
      const scaleY = page.offsetHeight > 0 ? page.offsetHeight / pageRect.height : 1

      const spans = Array.from(layer.querySelectorAll('span')).filter((span) =>
        (span.textContent || '').trim().length > 0
      )

      for (const span of spans) {
        const rawText = span.textContent || ''
        const matches = [...rawText.matchAll(globalRegex)]

        if (matches.length > 0 && span.firstChild && span.firstChild.nodeType === Node.TEXT_NODE) {
          totalMatches += matches.length
          for (const match of matches) {
            try {
              const range = document.createRange()
              range.setStart(span.firstChild, match.index)
              range.setEnd(span.firstChild, match.index + match[0].length)

              const rects = range.getClientRects()
              for (const rect of rects) {
                const div = document.createElement('div')
                div.className = 'pdf-search-highlight-overlay'
                div.style.position = 'absolute'
                div.style.left = `${(rect.left - pageRect.left) * scaleX}px`
                div.style.top = `${(rect.top - pageRect.top) * scaleY}px`
                div.style.width = `${rect.width * scaleX}px`
                div.style.height = `${rect.height * scaleY}px`
                div.style.backgroundColor = '#ffeb3b'
                div.style.mixBlendMode = 'multiply'
                div.style.pointerEvents = 'none'
                div.style.borderRadius = '2px'
                div.style.zIndex = '2'
                page.appendChild(div)
              }
            } catch (e) {
              // Skip if range creation fails
            }
          }
        }
      }
    })

    setHighlightCount(totalMatches)
  }, [clearDomHighlights])

  const calculateFitScale = useCallback((pdfWidth, pdfHeight, pageRotation = rotation) => {
    const rotated = pageRotation === 90 || pageRotation === 270
    const effectiveWidth = rotated ? pdfHeight : pdfWidth
    const effectiveHeight = rotated ? pdfWidth : pdfHeight

    const containerWidth = scrollContainerRef.current?.clientWidth || window.innerWidth
    const containerHeight = scrollContainerRef.current?.clientHeight || window.innerHeight

    const horizontalPadding = isMobile ? 20 : 40
    const verticalPadding = isMobile ? 20 : 40

    const availableWidth = Math.max(100, containerWidth - horizontalPadding)
    const availableHeight = Math.max(100, containerHeight - verticalPadding)

    const scaleByWidth = availableWidth / effectiveWidth
    const scaleByHeight = availableHeight / effectiveHeight

    return Math.max(0.1, Math.min(scaleByWidth, scaleByHeight))
  }, [isMobile, rotation])

  // Resize listener
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768
      setIsMobile(mobile)
      if (mobile && viewMode === 'continuous') {
        setViewMode('single')
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [viewMode])

  const pdfOptions = useMemo(() => ({
    cMapUrl: '/cmaps/',
    cMapPacked: true
  }), [])

  const PAGE_CHANGE_DELAY = 400

  const safeSetPage = useCallback((updater) => {
    const now = Date.now()
    // Block if within debounce window
    if (now - lastPageChangeRef.current < PAGE_CHANGE_DELAY) {
      return
    }
    // Block if page is currently loading
    if (isPageLoadingRef.current) {
      return
    }
    lastPageChangeRef.current = now
    isPageLoadingRef.current = true
    setPageNumber(updater)
  }, [])

  const onDocumentLoadSuccess = useCallback(({ numPages, pdf }) => {
    setNumPages(numPages)
    setIsDocumentReady(true)
    pdfDocumentRef.current = pdf
    isPageLoadingRef.current = false
  }, [])

  const onPageLoadSuccess = useCallback((page) => {
    const { width, height } = page

    if (!initialScaleSet || !hasManualZoom) {
      const autoFitKey = `${pageNumber}-${rotation}`

      if (lastAutoFitKeyRef.current !== autoFitKey) {
        const fitScale = calculateFitScale(width, height, rotation)
        setScale(fitScale)
        lastAutoFitKeyRef.current = autoFitKey
      }

      setInitialScaleSet(true)
    }

    isPageLoadingRef.current = false
  }, [calculateFitScale, hasManualZoom, initialScaleSet, pageNumber, rotation])

  const onPageRenderError = useCallback((error) => {
    // Silently handle render errors - common when rapidly changing pages
    if (error?.message?.includes('sendWithPromise') || error?.message?.includes('Worker')) {
      // Worker was destroyed - this is expected during rapid navigation
    } else {
      console.error('Page render failed:', error)
    }
    isPageLoadingRef.current = false
  }, [])

  const getPortalRoot = () => {
    let portalRoot = document.getElementById('file-preview-portal-root')
    if (!portalRoot) {
      portalRoot = document.createElement('div')
      portalRoot.id = 'file-preview-portal-root'
      document.body.appendChild(portalRoot)
    }
    portalRoot.style.position = 'relative'
    portalRoot.style.zIndex = '999999'
    return portalRoot
  }

  const setNextPage = () =>
    safeSetPage(prev => Math.min(prev + 1, numPages))
  const setPrevPage = () =>
    safeSetPage(prev => Math.max(prev - 1, 1))
  const setFirstPage = () => safeSetPage(() => 1)
  const setLastPage = () => safeSetPage(() => numPages)
  const handleZoom = newScale => {
    if (zoomTimeout.current) clearTimeout(zoomTimeout.current)
    zoomTimeout.current = setTimeout(() => {
      setHasManualZoom(true)
      lastAutoFitKeyRef.current = null
      shouldCenterAfterZoomRef.current = true
      setScale(newScale)
    }, 150)
  }
  const handleZoomIn = () => {
    setHasManualZoom(true)
    lastAutoFitKeyRef.current = null
    shouldCenterAfterZoomRef.current = true
    setScale(prev => Math.min(5, Number(prev) + 0.25))
  }
  const handleZoomOut = () => {
    setHasManualZoom(true)
    lastAutoFitKeyRef.current = null
    shouldCenterAfterZoomRef.current = true
    setScale(prev => Math.max(0.1, Number(prev) - 0.25))
  }

  // Go to specific page (works in both single and continuous modes)
  const goToPage = useCallback((page) => {
    if (page < 1 || page > numPages) return
    setPageNumber(page)

    if (viewMode === 'continuous') {
      const element = document.getElementById(`pdf-page-${page}`)
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [viewMode, numPages])

  // Text extraction for search - uses ref to avoid re-renders
  const onPageTextContent = useCallback((pageNum, textContent) => {
    try {
      if (textContent && textContent.items) {
        textItemsRef.current[pageNum] = textContent.items
        setHasExtractedText(true)
      }
    } catch (e) {
      // Silently ignore errors during text extraction
    }
  }, [])

  // Search function - reads from ref, no textItems dependency
  const handleSearch = useCallback((query, options = {}) => {
    const { navigate = true, progressiveFallback = false } = options

    if (!query || query.length < 2) {
      setSearchResults([])
      setCurrentMatch(0)
      clearDomHighlights()
      return
    }

    const trimmedQuery = query.trim()
    const escaped = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const isAlphanumeric = /^[a-z0-9]+$/i.test(trimmedQuery)

    // Word boundary at START only — prevents "50" matching inside "1500"
    // but allows prefix matching ("pow" finds "Power")
    const regex = isAlphanumeric
      ? new RegExp(`\\b${escaped}`, 'i')
      : new RegExp(escaped, 'i')

    // Normalized fallback for special char queries (e.g., "£5,000")
    const normalizedQuery = normalizeForSearch(query)
    const hasSpecialChars = trimmedQuery.toLowerCase() !== normalizedQuery

    const globalRegex = isAlphanumeric
      ? new RegExp(`\\b${escaped}`, 'gi')
      : new RegExp(escaped, 'gi')

    const matches = []
    Object.entries(textItemsRef.current).forEach(([pageNum, items]) => {
      if (!items) return

      items.forEach((item, idx) => {
        const itemText = item?.str || ''
        // Count each individual occurrence within this text item
        const itemMatches = [...itemText.matchAll(globalRegex)]

        for (const match of itemMatches) {
          matches.push({
            pageNumber: parseInt(pageNum, 10),
            itemIndex: idx,
            text: match[0]
          })
        }

        // Fallback for special character queries (e.g., "£5,000")
        if (itemMatches.length === 0 && hasSpecialChars && normalizedQuery.length > 0) {
          if (normalizeForSearch(itemText).includes(normalizedQuery)) {
            matches.push({
              pageNumber: parseInt(pageNum, 10),
              itemIndex: idx,
              text: itemText
            })
          }
        }
      })
    })

    // Progressive word removal fallback — only for pre-populated searches
    // (guru citations, calendar file clicks). Handles text split across
    // table cells, line breaks, multiple text items, etc.
    if (matches.length === 0 && progressiveFallback) {
      // Build a concatenated text per page and a mapping of position → item index
      // so we can match across multiple text items (not just within one item)
      const pageTexts = {}
      const pageItemMap = {} // for each page: array of {start, end, idx}

      Object.entries(textItemsRef.current).forEach(([pageNum, items]) => {
        if (!items) return
        let pageText = ''
        const itemMap = []
        items.forEach((item, idx) => {
          const str = (item?.str || '') + ' '
          itemMap.push({ start: pageText.length, end: pageText.length + str.length, idx })
          pageText += str
        })
        pageTexts[pageNum] = pageText
        pageItemMap[pageNum] = itemMap
      })

      const findInConcatenated = (searchStr) => {
        if (!searchStr || searchStr.length < 3) return false
        const escaped = searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        // Allow flexible whitespace (newlines, tabs, multiple spaces)
        const flexible = escaped.replace(/\s+/g, '\\s+')
        const flexRegex = new RegExp(flexible, 'gi')
        let found = false
        Object.entries(pageTexts).forEach(([pageNum, pageText]) => {
          const pageMatches = [...pageText.matchAll(flexRegex)]
          for (const m of pageMatches) {
            const matchStart = m.index
            const itemMap = pageItemMap[pageNum]
            const item = itemMap.find(i => matchStart >= i.start && matchStart < i.end)
            if (item) {
              matches.push({
                pageNumber: parseInt(pageNum, 10),
                itemIndex: item.idx,
                text: m[0]
              })
              found = true
            }
          }
        })
        return found
      }

      // Try the full query first with flexible whitespace
      if (!findInConcatenated(trimmedQuery)) {
        // Progressive word removal
        const words = trimmedQuery.split(/\s+/)
        const minWords = Math.max(2, Math.ceil(words.length / 4))

        for (let len = words.length - 1; len >= minWords; len--) {
          // Try removing from end
          const fromStart = words.slice(0, len).join(' ')
          if (findInConcatenated(fromStart)) break

          // Try removing from start
          const fromEnd = words.slice(words.length - len).join(' ')
          if (fromEnd !== fromStart && findInConcatenated(fromEnd)) break

          // Try a sliding window in the middle
          if (len < words.length - 1) {
            for (let start = 1; start <= words.length - len - 1; start++) {
              const middle = words.slice(start, start + len).join(' ')
              if (findInConcatenated(middle)) break
            }
            if (matches.length > 0) break
          }
        }
      }
    }

    setSearchResults(matches)
    setCurrentMatch(0)

    if (navigate && matches.length > 0) {
      goToPage(matches[0].pageNumber)
    }
  }, [goToPage, normalizeForSearch, clearDomHighlights])

  useEffect(() => {
    if (!open || !isDocumentReady || !numPages || !pdfDocumentRef.current) return

    let cancelled = false

    const extractAllPagesText = async () => {
      for (let page = 1; page <= numPages; page += 1) {
        if (cancelled || textItemsRef.current[page]) continue
        try {
          const pdfPage = await pdfDocumentRef.current.getPage(page)
          const textContent = await pdfPage.getTextContent()
          textItemsRef.current[page] = textContent?.items || []
        } catch (e) {
          // ignore extraction errors for individual pages
        }
      }

      if (!cancelled && searchTextRef.current?.length >= 2) {
        handleSearch(searchTextRef.current, { navigate: false })
      }
    }

    extractAllPagesText()

    return () => {
      cancelled = true
    }
  }, [open, isDocumentReady, numPages, handleSearch])

  useEffect(() => {
    if (!open) return

    const timer = setTimeout(() => {
      applyDomHighlights(searchText)
    }, 120)

    return () => clearTimeout(timer)
  }, [open, searchText, pageNumber, viewMode, applyDomHighlights])

  // Debounced search
  const onSearchChange = useCallback((e) => {
    const value = e.target.value
    searchTextRef.current = value
    setSearchText(value)
    hasNavigatedRef.current = false

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current)
    }

    searchTimeout.current = setTimeout(() => {
      handleSearch(value, { navigate: false })
    }, 300)
  }, [handleSearch])

  // Navigate between matches
  const navigateToMatch = useCallback((index) => {
    if (searchResults.length === 0 || index < 0 || index >= searchResults.length) return
    setCurrentMatch(index)
    hasNavigatedRef.current = true
    goToPage(searchResults[index].pageNumber)
  }, [searchResults, goToPage])

  const nextMatch = useCallback(() => {
    if (searchResults.length === 0) return
    if (!hasNavigatedRef.current) {
      // First navigation - go to first match
      navigateToMatch(0)
    } else {
      const next = (currentMatch + 1) % searchResults.length
      navigateToMatch(next)
    }
  }, [currentMatch, searchResults, navigateToMatch])

  const prevMatch = useCallback(() => {
    if (searchResults.length === 0) return
    if (!hasNavigatedRef.current) {
      // First navigation - go to last match
      navigateToMatch(searchResults.length - 1)
    } else {
      const prev = currentMatch === 0 ? searchResults.length - 1 : currentMatch - 1
      navigateToMatch(prev)
    }
  }, [currentMatch, searchResults, navigateToMatch])

  // Toggle search box - switch to continuous mode when opening search
  // so all pages are visible and highlights work across the entire document
  const toggleSearch = useCallback(() => {
    setShowSearch(prev => !prev)
    if (!showSearch) {
      if (!isMobile && viewMode !== 'continuous') {
        setViewMode('continuous')
      }
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }
  }, [showSearch, isMobile, viewMode])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return

      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault()
        toggleSearch()
      }
      if (e.key === 'Escape' && showSearch) {
        setShowSearch(false)
        setSearchText('')
        searchTextRef.current = ''
        setSearchResults([])
        setHighlightCount(0)
        clearDomHighlights()
      }
      if (e.key === 'Enter' && showSearch) {
        if (searchResults.length > 0) {
          // Already have results - navigate to next/previous match
          e.shiftKey ? prevMatch() : nextMatch()
        } else if (searchTextRef.current?.length >= 2) {
          // No results yet - run initial search
          handleSearch(searchTextRef.current)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, showSearch, toggleSearch, nextMatch, prevMatch, handleSearch, searchResults.length])

  useEffect(() => {
    const handleMouseMove = (e) => {
      const container = scrollContainerRef.current
      if (!container || !dragStateRef.current.active) return

      const dx = e.clientX - dragStateRef.current.startX
      const dy = e.clientY - dragStateRef.current.startY

      container.scrollLeft = dragStateRef.current.scrollLeft - dx
      container.scrollTop = dragStateRef.current.scrollTop - dy
    }

    const stopDragging = () => {
      if (!dragStateRef.current.active) return
      dragStateRef.current.active = false
      setIsDragging(false)
      document.body.style.userSelect = ''
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', stopDragging)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', stopDragging)
    }
  }, [])

  // Close search on click/tap outside
  useEffect(() => {
    if (!showSearch) return

    const handleTapOutside = (e) => {
      // Ignore taps on the search bar itself or on the search toggle button
      if (searchBarRef.current && searchBarRef.current.contains(e.target)) return
      if (e.target.closest('[title="Search (Ctrl+F)"]')) return

      setShowSearch(false)
      setSearchText('')
      searchTextRef.current = ''
      setSearchResults([])
      setHighlightCount(0)
      clearDomHighlights()
    }

    // Use timeout so the opening tap doesn't immediately close it
    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', handleTapOutside)
    }, 100)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('pointerdown', handleTapOutside)
    }
  }, [showSearch, clearDomHighlights])

  useEffect(() => {
    if (!open || !shouldCenterAfterZoomRef.current || viewMode !== 'single') return

    let attempt = 0
    let rafId = null
    let timeoutId = null

    const centerAfterRender = () => {
      const container = scrollContainerRef.current
      if (!container) return

      const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth)
      const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight)

      if (maxScrollLeft > 0 || maxScrollTop > 0 || attempt >= 4) {
        container.scrollLeft = Math.floor(maxScrollLeft / 2)
        container.scrollTop = Math.floor(maxScrollTop / 2)
        shouldCenterAfterZoomRef.current = false
        return
      }

      attempt += 1
      timeoutId = setTimeout(() => {
        rafId = requestAnimationFrame(centerAfterRender)
      }, 35)
    }

    rafId = requestAnimationFrame(centerAfterRender)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [open, scale, viewMode, pageNumber])

  useEffect(() => {
    if (!open || viewMode !== 'single') return

    let rafId = null
    const measureOverflow = () => {
      const container = scrollContainerRef.current
      if (!container) return

      const hasHorizontalOverflow = container.scrollWidth > container.clientWidth + 1
      const hasVerticalOverflow = container.scrollHeight > container.clientHeight + 1
      setIsOverflowingHorizontallyInSingleView(hasHorizontalOverflow)
      setIsOverflowingVerticallyInSingleView(hasVerticalOverflow)
    }

    rafId = requestAnimationFrame(measureOverflow)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [open, viewMode, pageNumber, scale, rotation, showThumbnails, isMobile])

  // Intersection Observer for continuous scroll mode - debounced to prevent spam
  useEffect(() => {
    if (viewMode !== 'continuous' || !isDocumentReady) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const pageNum = parseInt(entry.target.dataset.page)
            if (!isNaN(pageNum)) {
              // Debounce page number updates
              if (intersectionDebounceRef.current) {
                clearTimeout(intersectionDebounceRef.current)
              }
              intersectionDebounceRef.current = setTimeout(() => {
                setPageNumber(pageNum)
              }, 150)
            }
          }
        })
      },
      { threshold: 0.5, root: scrollContainerRef.current }
    )

    const pages = document.querySelectorAll('[data-page]')
    pages.forEach(el => observer.observe(el))

    return () => {
      observer.disconnect()
      if (intersectionDebounceRef.current) {
        clearTimeout(intersectionDebounceRef.current)
      }
    }
  }, [viewMode, isDocumentReady, numPages])

  const handleRotateLeft = () => {
    if (!hasManualZoom && pdfDocumentRef.current) {
      pdfDocumentRef.current.getPage(pageNumber).then((page) => {
        const nextRotation = rotation === 0 ? 270 : rotation - 90
        setScale(calculateFitScale(page.width, page.height, nextRotation))
      }).catch(() => {})
    }
    setRotation(prev => {
      switch (prev) {
        case 0: return 270
        case 270: return 180
        case 180: return 90
        case 90: return 0
        default: return 0
      }
    })
  }

  const handleRotateRight = () => {
    if (!hasManualZoom && pdfDocumentRef.current) {
      pdfDocumentRef.current.getPage(pageNumber).then((page) => {
        const nextRotation = rotation === 270 ? 0 : rotation + 90
        setScale(calculateFitScale(page.width, page.height, nextRotation))
      }).catch(() => {})
    }
    setRotation(prev => {
      switch (prev) {
        case 0: return 90
        case 90: return 180
        case 180: return 270
        case 270: return 0
        default: return 0
      }
    })
  }

  const handleDownload = () => {
    if (!file?.url) return
    const anchor = document.createElement('a')
    anchor.href = file.url
    anchor.target = '_blank'
    anchor.download = file.displayName || file.name || 'document'
    anchor.click()

    if (skipAccessLog) return
    const fileId = file.id
    const fileVersionId = version ? version.id : pathOr('', ['versions', 0, 'id'], file)
    if (!fileId || !fileVersionId) return
    markFileAsViewed({ fileId, fileVersionId, type: 'download' })
  }

  useEffect(() => {
    if (open) {
      if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          if (skipAccessLog) return

          const fileId = file.id
          const fileVersionId = version
            ? version.id
            : pathOr('', ['versions', 0, 'id'], file)

          // Don't fire a malformed URL (double-slash) — the authenticated
          // log-access endpoint requires a version; public / source previews
          // don't have one and should be skipped entirely.
          if (!fileId || !fileVersionId) return

          markFileAsViewed({
            fileId,
            fileVersionId,
            type: 'view'
          })
        }, 0)
      }
    } else {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [open, file, version])

  // Ref to track if initial search has been triggered (used by auto-search effects below)
  const initialSearchDoneRef = useRef(false)

  // Reset state when viewer closes
  useEffect(() => {
    if (!open) {
      setIsDocumentReady(false)
      setNumPages(null)
      setPageNumber(1)
      setScale(1)
      setInitialScaleSet(false)
      setHasManualZoom(false)
      lastAutoFitKeyRef.current = null
      shouldCenterAfterZoomRef.current = false
      setRotation(0)
      setShowThumbnails(false)
      setViewMode('single')
      setShowSearch(false)
      setSearchText('')
      searchTextRef.current = ''
      setSearchResults([])
      clearDomHighlights()
      textItemsRef.current = {}
      pdfDocumentRef.current = null
      isPageLoadingRef.current = false
      initialSearchDoneRef.current = false
      setHasExtractedText(false)
    }
  }, [open, clearDomHighlights])

  useEffect(() => {
    if (!open || !searchTextRef.current || searchTextRef.current.length < 2) return

    // Longer delay when viewMode changes to give text layers time to render
    const timer = setTimeout(() => {
      applyDomHighlights(searchTextRef.current)
    }, 400)

    return () => clearTimeout(timer)
  }, [open, searchResults.length, viewMode, pageNumber, applyDomHighlights])

  // Re-apply highlights after continuous document finishes loading
  useEffect(() => {
    if (!isDocumentReady || !searchTextRef.current || searchTextRef.current.length < 2) return

    const timer = setTimeout(() => {
      applyDomHighlights(searchTextRef.current)
    }, 500)

    return () => clearTimeout(timer)
  }, [isDocumentReady, applyDomHighlights])

  // Auto-open search panel when initialSearchQuery is provided (content search mode)
  useEffect(() => {
    if (open && initialSearchQuery && initialSearchQuery.length >= 2) {
      setShowSearch(true)
      setSearchText(initialSearchQuery)
      searchTextRef.current = initialSearchQuery
      initialSearchDoneRef.current = false
    }
  }, [open, initialSearchQuery])

  // Execute the auto-search once page text has been extracted
  useEffect(() => {
    if (
      open &&
      hasExtractedText &&
      initialSearchQuery &&
      initialSearchQuery.length >= 2 &&
      !initialSearchDoneRef.current
    ) {
      initialSearchDoneRef.current = true
      handleSearch(initialSearchQuery, { progressiveFallback: true })
    }
  }, [open, hasExtractedText, initialSearchQuery, handleSearch])

  if (!open) return null

  const handlePreviewMouseDown = (e) => {
    if (e.button !== 0 || scale <= 1) return

    const container = scrollContainerRef.current
    if (!container) return

    dragStateRef.current.active = true
    dragStateRef.current.startX = e.clientX
    dragStateRef.current.startY = e.clientY
    dragStateRef.current.scrollLeft = container.scrollLeft
    dragStateRef.current.scrollTop = container.scrollTop

    setIsDragging(true)
    document.body.style.userSelect = 'none'
    e.preventDefault()
  }

  return ReactDOM.createPortal(
    <PreviewWrapper open={open} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
      <PreviewOptions>
        <TopToolbar>
          <ToolbarSection>
            <ThumbnailToggle
              onClick={() => setShowThumbnails(!showThumbnails)}
              $active={showThumbnails}
              title="Toggle thumbnails"
            />
            {!isMobile && (
              <ViewModeToggle
                onClick={() => setViewMode(viewMode === 'single' ? 'continuous' : 'single')}
                $active={viewMode === 'continuous'}
                title={viewMode === 'single' ? 'Switch to continuous scroll' : 'Switch to single page'}
              />
            )}
          </ToolbarSection>

          {viewMode === 'single' && (
            <ToolbarSection>
              {!isMobile && <FirstPage onClick={setFirstPage} />}
              <PrevIcon onClick={setPrevPage} />
              <PageInfo>
                <PageInput
                  type='number'
                  value={pageNumber}
                  min={1}
                  max={numPages}
                  onChange={e => goToPage(Number(e.target.value))}
                />
                <span>/ {numPages || '-'}</span>
              </PageInfo>
              <NextIcon onClick={setNextPage} />
              {!isMobile && <LastPage onClick={setLastPage} />}
            </ToolbarSection>
          )}

          <ToolbarSection>
            <ZoomOut onClick={handleZoomOut} />
            {!isMobile && (
              <ZoomSlider
                type="range"
                min={0.1}
                max={5}
                step={0.1}
                value={scale}
                onChange={e => handleZoom(Number(e.target.value))}
              />
            )}
            <ZoomIn onClick={handleZoomIn} />
          </ToolbarSection>

          {!isMobile && (
            <ToolbarSection>
              <RotateLeft onClick={handleRotateLeft} />
              <RotateRight onClick={handleRotateRight} />
            </ToolbarSection>
          )}

          <ToolbarSection>
            <SearchToggle onClick={toggleSearch} $active={showSearch} title="Search (Ctrl+F)" />
            <DownloadAction onClick={handleDownload} title="Download" />
          </ToolbarSection>

          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </TopToolbar>

        {showSearch && (
          <SearchBarWrapper ref={searchBarRef}>
            <SearchBox>
              <SearchInput
                ref={searchInputRef}
                type="text"
                placeholder="Search in document..."
                value={searchText}
                onChange={onSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()
                    if (searchResults.length > 0) {
                      e.shiftKey ? prevMatch() : nextMatch()
                    } else if (searchTextRef.current?.length >= 2) {
                      handleSearch(searchTextRef.current)
                    }
                  }
                }}
              />
              {(searchResults.length > 0 || highlightCount > 0) && (
                <SearchNav>
                  <MatchCount>{currentMatch + 1} / {highlightCount}</MatchCount>
                  <SearchNavButton onClick={prevMatch} title="Previous match">
                    <KeyboardArrowUpIcon fontSize="small" />
                  </SearchNavButton>
                  <SearchNavButton onClick={nextMatch} title="Next match">
                    <KeyboardArrowDownIcon fontSize="small" />
                  </SearchNavButton>
                </SearchNav>
              )}
              {searchText && searchResults.length === 0 && highlightCount === 0 && (
                <NoResults>No results</NoResults>
              )}
            </SearchBox>
          </SearchBarWrapper>
        )}
      </PreviewOptions>

      <GlobalSearchHighlightStyles>
        <ViewerContainer>
          <ThumbnailSidebar $visible={showThumbnails} $isMobile={isMobile}>
            {showThumbnails && open && numPages && (
              <ReactPdfDocument
                key={`thumb_${file.url}`}
                file={file.url}
                options={pdfOptions}
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <ThumbnailItem
                    key={i + 1}
                    $active={pageNumber === i + 1}
                    onClick={() => goToPage(i + 1)}
                  >
                    <Page
                      pageNumber={i + 1}
                      width={isMobile ? 65 : 120}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                    <ThumbnailLabel>{i + 1}</ThumbnailLabel>
                  </ThumbnailItem>
                ))}
              </ReactPdfDocument>
            )}
          </ThumbnailSidebar>

          <PreviewContentWrapper
            ref={scrollContainerRef}
            $continuous={viewMode === 'continuous'}
            $canPan={scale > 1}
            $dragging={isDragging}
            $singlePageOverflowingX={isOverflowingHorizontallyInSingleView}
            $singlePageOverflowingY={isOverflowingVerticallyInSingleView}
            onMouseDown={handlePreviewMouseDown}
          >
            {viewMode === 'continuous' ? (
              <ContinuousScrollContainer>
                {open && numPages && (
                  <ReactPdfDocument
                    key={`cont_${file.url}`}
                    file={file.url}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <Loading>
                        <CircularProgress />
                      </Loading>
                    }
                    options={pdfOptions}
                  >
                    {Array.from({ length: numPages }, (_, i) => (
                      <PageWrapper
                        key={`${i + 1}`}
                        id={`pdf-page-${i + 1}`}
                        data-page={i + 1}
                      >
                        <Page
                          pageNumber={i + 1}
                          scale={scale}
                          rotate={rotation}
                          renderTextLayer={true}
                          renderAnnotationLayer={false}
                          onGetTextSuccess={(textContent) => onPageTextContent(i + 1, textContent)}
                        />
                      </PageWrapper>
                    ))}
                  </ReactPdfDocument>
                )}
              </ContinuousScrollContainer>
            ) : (
              <SinglePageContainer>
                {open && (
                  <ReactPdfDocument
                    key={file.url}
                    file={file.url}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(error) => console.error('PDF load error:', error)}
                    loading={
                      <Loading>
                        <CircularProgress />
                      </Loading>
                    }
                    error={<ErrorMessage>Failed to load PDF</ErrorMessage>}
                    renderMode="canvas"
                    options={pdfOptions}
                  >
                    {isDocumentReady && numPages && pageNumber >= 1 && pageNumber <= numPages && (
                      <Page
                        key={`page_${pageNumber}`}
                        scale={scale}
                        pageNumber={pageNumber}
                        rotate={rotation}
                        renderAnnotationLayer={false}
                        renderTextLayer={true}
                        onLoadSuccess={onPageLoadSuccess}
                        onRenderError={onPageRenderError}
                        onGetTextSuccess={(textContent) => onPageTextContent(pageNumber, textContent)}
                      />
                    )}
                  </ReactPdfDocument>
                )}
              </SinglePageContainer>
            )}
          </PreviewContentWrapper>
        </ViewerContainer>
      </GlobalSearchHighlightStyles>
    </PreviewWrapper>,
    getPortalRoot()
  )
}

export default PdfFullScreenViewer

const PreviewWrapper = styled.div`
  box-sizing: border-box;
  background-color: rgba(30, 30, 30, 0.95);
  display: none;
  transition: all 0.3s;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  ${({ open }) =>
    open &&
    css`
      display: flex;
      flex-direction: column;
      z-index: 999999;
    `}
`

const PreviewOptions = styled.div`
  width: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`

const TopToolbar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50px;
  padding: 0 60px 0 10px;
  gap: 10px;

  @media (max-width: 768px) {
    justify-content: space-between;
    padding: 8px 50px 8px 8px;
    gap: 5px;
  }
`

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  @media (max-width: 768px) {
    gap: 2px;
  }
`

const CloseButton = styled.div`
  position: absolute;
  top: 8px;
  right: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  svg {
    font-size: 24px;
    color: ${({ theme }) => theme.colors?.haiti || '#1a1a2e'};
  }
`

const IconButton = css`
  font-size: 22px !important;
  color: ${({ theme }) => theme.colors?.haiti || '#1a1a2e'};
  cursor: pointer;
  transition: all 0.2s;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors?.primary || '#33becc'};
    background-color: rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 768px) {
    font-size: 20px !important;
    padding: 2px;
  }
`

const ThumbnailToggle = styled(ViewListIcon)`
  ${IconButton}
  color: ${({ $active, theme }) => $active ? (theme.colors?.primary || '#33becc') : (theme.colors?.haiti || '#1a1a2e')};
`

const ViewModeToggle = styled(ViewStreamIcon)`
  ${IconButton}
  color: ${({ $active, theme }) => $active ? (theme.colors?.primary || '#33becc') : (theme.colors?.haiti || '#1a1a2e')};
`

const FirstPage = styled(FirstPageIcon)`
  ${IconButton}
`

const PrevIcon = styled(NavigateBeforeIcon)`
  ${IconButton}
  font-size: 24px !important;

  @media (max-width: 768px) {
    font-size: 26px !important;
  }
`

const NextIcon = styled(NavigateNextIcon)`
  ${IconButton}
  font-size: 24px !important;

  @media (max-width: 768px) {
    font-size: 26px !important;
  }
`

const LastPage = styled(LastPageIcon)`
  ${IconButton}
`

const ZoomOut = styled(ZoomOutIcon)`
  ${IconButton}
`

const ZoomIn = styled(ZoomInIcon)`
  ${IconButton}
`

const RotateLeft = styled(RotateLeftIcon)`
  ${IconButton}
`

const RotateRight = styled(RotateRightIcon)`
  ${IconButton}
`

const SearchToggle = styled(SearchIcon)`
  ${IconButton}
  color: ${({ $active, theme }) => $active ? (theme.colors?.primary || '#33becc') : (theme.colors?.haiti || '#1a1a2e')};
`

const DownloadAction = styled(FileDownloadOutlinedIcon)`
  ${IconButton}
`

const PageInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  span {
    white-space: nowrap;
    font-size: 14px;
    color: ${({ theme }) => theme.colors?.haiti || '#1a1a2e'};
  }
`

const PageInput = styled.input`
  text-align: center;
  -moz-appearance: textfield;
  width: 45px;
  padding: 6px 4px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors?.primary || '#33becc'};
  }

  @media (max-width: 768px) {
    width: 40px;
    padding: 4px 2px;
  }
`

const ZoomSlider = styled.input`
  width: 80px;
  cursor: pointer;
`

const SearchBarWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  background-color: rgba(255, 255, 255, 0.95);
`

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 4px 12px;
  width: 100%;
  max-width: 400px;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors?.primary || '#33becc'};
  }
`

const SearchInput = styled.input`
  border: none;
  outline: none;
  padding: 6px 8px;
  font-size: 14px;
  flex: 1;
  min-width: 0;

  &::placeholder {
    color: #aaa;
  }
`

const SearchNav = styled.div`
  display: flex;
  align-items: center;
  margin-left: 8px;
  border-left: 1px solid #ddd;
  padding-left: 8px;
  gap: 2px;
`

const MatchCount = styled.span`
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  margin-right: 4px;
`

const SearchNavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors?.haiti || '#1a1a2e'};
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors?.primary || '#33becc'};
    background-color: rgba(0, 0, 0, 0.05);
  }
`

const NoResults = styled.span`
  font-size: 12px;
  color: #999;
  margin-left: 8px;
  white-space: nowrap;
`

const ViewerContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`

const ThumbnailSidebar = styled.div`
  width: ${props => props.$visible ? (props.$isMobile ? '85px' : '140px') : '0'};
  min-width: ${props => props.$visible ? (props.$isMobile ? '85px' : '140px') : '0'};
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background: rgba(40, 40, 40, 0.95);
  border-right: 1px solid #333;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: ${props => props.$visible ? '10px 6px' : '0'};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
`

const ThumbnailItem = styled.div`
  cursor: pointer;
  border: 2px solid ${props => props.$active ? '#33becc' : 'transparent'};
  border-radius: 4px;
  padding: 4px;
  background: ${props => props.$active ? 'rgba(51, 190, 204, 0.15)' : 'transparent'};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;

  &:hover {
    border-color: #33becc;
    background: rgba(51, 190, 204, 0.1);
  }

  .react-pdf__Page {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  canvas {
    max-width: 100% !important;
    height: auto !important;
  }
`

const ThumbnailLabel = styled.div`
  text-align: center;
  color: #ccc;
  font-size: 11px;
  margin-top: 4px;
`

const PreviewContentWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: ${({ $continuous, $singlePageOverflowingX }) => (
    $continuous || $singlePageOverflowingX ? 'flex-start' : 'center'
  )};
  align-items: ${({ $continuous, $singlePageOverflowingY }) => (
    $continuous || $singlePageOverflowingY ? 'flex-start' : 'center'
  )};
  overflow: auto;
  padding: 20px;
  background: rgba(50, 50, 50, 0.5);
  cursor: ${({ $dragging, $canPan }) => ($dragging ? 'grabbing' : ($canPan ? 'grab' : 'default'))};

  @media (max-width: 768px) {
    padding: 10px;
    align-items: flex-start;
  }
`

const SinglePageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: max-content;
  height: max-content;
  min-width: max-content;
  min-height: max-content;
  flex-shrink: 0;

  .react-pdf__Page {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    flex-shrink: 0;
  }
`

const ContinuousScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding-bottom: 40px;
`

const PageWrapper = styled.div`
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  background: white;

  .react-pdf__Page {
    display: block;
  }
`

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 300px;
`

const ErrorMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: #ff6b6b;
  font-size: 16px;
`

const GlobalSearchHighlightStyles = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;

  .pdf-search-highlight-overlay {
    position: absolute;
    background-color: #ffeb3b;
    mix-blend-mode: multiply;
    pointer-events: none;
    border-radius: 2px;
  }
`
